import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import Sidebar from './SidebarNavigation';
import ThemeToggle from '../ThemeToggle';
import { MarketTicker } from './MarketTicker'; 
import {
  NavScannerIcon,
  NavWatchlistIcon,
  NavAlertsIcon,
  NavEventsIcon,
} from '../icons/NavIcons';
import { useMarketStatus } from '../../utils/marketHours';
import { useMarketIndices } from '../../hooks/useMarketIndices';
import { api } from '../../services/api';

const BOTTOM_NAV = [
  { label: 'Scanner',   path: '/scanner',   icon: NavScannerIcon   },
  { label: 'Watchlist', path: '/watchlist', icon: NavWatchlistIcon  },
  { label: 'Alerts',    path: '/alerts',    icon: NavAlertsIcon    },
  { label: 'Events',    path: '/events',    icon: NavEventsIcon    },
];

const MARKET_STATUS_CONFIG = {
  'open':        { label: 'Market · Open',        dot: 'bg-emerald-500 dark:bg-[#10d98e]',   ping: 'bg-emerald-400',   badge: 'bg-emerald-50 dark:bg-[#10d98e]/10 border-emerald-200 dark:border-[#10d98e]/25', text: 'text-emerald-700 dark:text-[#10d98e]' },
  'pre-market':  { label: 'Market · Pre-Market',  dot: 'bg-yellow-400 dark:bg-yellow-300',   ping: 'bg-yellow-300',    badge: 'bg-yellow-50 dark:bg-yellow-400/10 border-yellow-200 dark:border-yellow-400/25',  text: 'text-yellow-700 dark:text-yellow-300' },
  'after-hours': { label: 'Market · After-Hours', dot: 'bg-orange-400 dark:bg-orange-300',   ping: 'bg-orange-300',    badge: 'bg-orange-50 dark:bg-orange-400/10 border-orange-200 dark:border-orange-400/25',  text: 'text-orange-700 dark:text-orange-300' },
  'closed':      { label: 'Market · Closed',      dot: 'bg-gray-400 dark:bg-gray-500',       ping: 'bg-gray-300',      badge: 'bg-gray-50 dark:bg-gray-500/10 border-gray-200 dark:border-gray-500/25',         text: 'text-gray-500 dark:text-gray-400' },
} as const;
export default function RootLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const marketStatus = useMarketStatus();
  const marketCfg = MARKET_STATUS_CONFIG[marketStatus];
  const { indices, isError: indicesError } = useMarketIndices();
  const [counts, setCounts] = useState({ all: 0, catalyst: 0, momentum: 0, none: 0, gainers: 0, losers: 0, watchlist: 0 });

  // Keep watchlist badge in sync even when the watchlist page is not mounted
  useEffect(() => {
    let cancelled = false;
    const loadWatchlistCount = async () => {
      try {
        const data = await api.getWatchlist();
        if (!cancelled) {
          setCounts((prev) => ({ ...prev, watchlist: (data.items || []).length }));
        }
      } catch {
        // leave previous count on failure
      }
    };
    void loadWatchlistCount();
    const intervalId = window.setInterval(loadWatchlistCount, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="flex min-h-screen bg-[#f4f7fb] dark:bg-[#080c14] text-gray-900 dark:text-[#f1f5fb] font-sans selection:bg-blue-500/20">

      {/* Sidebar — z-50 */}
    <Sidebar 
      mobileOpen={sidebarOpen} 
      onClose={() => setSidebarOpen(false)} 
      scannerCount={counts.all}
      gainersCount={counts.gainers}
      losersCount={counts.losers}
      watchlistCount={counts.watchlist}
    />      
      {/* Mobile backdrop overlay */}
      <div
        className={`fixed inset-0 z-[45] bg-black/50 backdrop-blur-[2px] md:hidden transition-opacity duration-300 ${
          sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Main column */}
      <div className="flex-1 flex flex-col md:ml-64 min-w-0">

        {/* ── Mobile header — z-40: below overlay when sidebar is open ── */}
        <header className="
          fixed top-0 left-0 right-0 z-40 md:hidden
          flex items-center justify-between h-[56px] px-3
          bg-white/95 dark:bg-[#0b1020]/95 backdrop-blur-md
          border-b border-gray-200/80 dark:border-[#1d2d47]
        ">
          {/* Logo — full hex icon + wordmark */}
          <NavLink
            to="/scanner"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-2.5 hover:opacity-90 transition-opacity"
          >
            <svg width="30" height="30" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="mhg" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#4f8ff8" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
              </defs>
              <polygon
                points="18,2 32,10 32,26 18,34 4,26 4,10"
                fill="rgba(79,143,248,0.08)"
                stroke="url(#mhg)"
                strokeWidth="1.8"
              />
              <path
                d="M20 8L13 19.5H18.5L16 28L23 16.5H17.5L20 8Z"
                fill="url(#mhg)"
              />
            </svg>
            <div className="flex items-baseline leading-none">
              <span className="text-[16px] font-bold tracking-tight text-gray-900 dark:text-[#f1f5fb]">
                Catalyst
              </span>
              <span
                className="text-[16px] font-bold tracking-tight"
                style={{
                  background: 'linear-gradient(135deg, #4f8ff8 0%, #6366f1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                IQ
              </span>
            </div>
          </NavLink>

          <div className="flex items-center gap-1">
            <ThemeToggle />
            <button
              className="p-2 rounded-lg text-gray-500 dark:text-[#c2d0e0] hover:text-gray-900 dark:hover:text-[#f1f5fb] hover:bg-gray-100 dark:hover:bg-[#141e30] transition-colors"
              onClick={() => setSidebarOpen(o => !o)}
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
            >
              {sidebarOpen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="4" x2="20" y1="6"  y2="6"  />
                  <line x1="4" x2="20" y1="12" y2="12" />
                  <line x1="4" x2="20" y1="18" y2="18" />
                </svg>
              )}
            </button>
          </div>
        </header>

        {/* ── 2. Desktop header — חוזר ל-top-0 המקורי ── */}
        <header className="hidden md:flex fixed top-0 left-64 right-0 z-40 h-[60px] items-center justify-end gap-3 px-6 bg-white/90 dark:bg-[#0b1020]/90 backdrop-blur-md border-b border-gray-200/80 dark:border-[#1d2d47]">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#4f8ff8] via-[#a78bfa] to-[#10d98e] opacity-60" />

          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${marketCfg.badge}`}>
            <span className="relative flex h-2 w-2">
              {marketStatus === 'open' && (
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${marketCfg.ping} opacity-75`} />
              )}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${marketCfg.dot}`} />
            </span>
            <span className={`text-[11px] font-semibold tracking-wide uppercase font-mono ${marketCfg.text}`}>
              {marketCfg.label}
            </span>
          </div>

          <div className="w-px h-5 bg-gray-200 dark:bg-[#1d2d47]" />
          <ThemeToggle />
        </header>

        {/* ── 3. סרגל המדדים העליון — זז אל מתחת לכפתור Market Open ── */}
        {/* במובייל מתחת ל-Header (top-56px), ובדסקטופ מתחת ל-Header (top-60px) */}
        <div className="fixed top-[56px] md:top-[60px] left-0 md:left-64 right-0 z-30">
          <MarketTicker indices={indices} isError={indicesError} />
        </div>

        {/* ── 4. Page content ── */}
        {/* pt דוחף את התוכן הדינמי למטה מתחת ל-Header ולסרגל המשולבים */}
        <main className="flex-1 pt-[92px] md:pt-[96px] pb-[64px] md:pb-0 w-full max-w-[1680px] mx-auto">
          <div className="p-4 sm:p-6 md:p-8 animate-fade-in-up">
          <Outlet context={{ setCounts }} /> 
          </div>
        </main>

        {/* ── Footer ── */}
        <footer className="hidden md:flex w-full px-6 md:px-8 py-3 border-t border-gray-200/60 dark:border-[#1d2d47] bg-white/60 dark:bg-[#0b1020]/60 items-center justify-between gap-4">
          <span className="text-[10.5px] font-mono text-gray-400 dark:text-[#a3b6cc]">
            © 2026 Diversitech Catalysts
          </span>
          <span className="text-[11px] font-bold font-mono tracking-widest bg-gradient-to-r from-[#4f8ff8] via-[#a78bfa] to-[#10d98e] bg-clip-text text-transparent">
            CATALYSTIQ
          </span>
          <span className="text-[10.5px] font-mono text-gray-300 dark:text-[#1d2d47]">
            v0.4
          </span>
        </footer>

        {/* ── Mobile bottom navigation ── */}
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 md:hidden flex items-stretch h-[60px] bg-white/95 dark:bg-[#0b1020]/95 backdrop-blur-md border-t border-gray-200/80 dark:border-[#1d2d47]"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {BOTTOM_NAV.map(({ label, path, icon: Icon }) => {
            const isActive =
              location.pathname === path ||
              (path === '/scanner' && (location.pathname === '/' || location.pathname === ''));

            return (
              <NavLink
                key={path}
                to={path}
                className={`
                  flex-1 flex flex-col items-center justify-center gap-[3px]
                  transition-colors duration-150 relative
                  ${isActive
                    ? 'text-[#4f8ff8]'
                    : 'text-gray-400 dark:text-[#a3b6cc] hover:text-gray-700 dark:hover:text-[#c2d0e0]'
                  }
                `}
              >
                {isActive && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2px] rounded-b-full bg-gradient-to-r from-[#4f8ff8] to-[#6366f1]" />
                )}
                <span className="w-[18px] h-[18px]">
                  <Icon />
                </span>
                <span className={`text-[9.5px] font-semibold tracking-tight ${isActive ? 'text-[#4f8ff8]' : ''}`}>
                  {label}
                </span>
              </NavLink>
            );
          })}

          <button
            onClick={() => setSidebarOpen(o => !o)}
            className={`
              flex-1 flex flex-col items-center justify-center gap-[3px]
              transition-colors duration-150 relative
              ${sidebarOpen
                ? 'text-[#a78bfa]'
                : 'text-gray-400 dark:text-[#a3b6cc] hover:text-gray-700 dark:hover:text-[#c2d0e0]'
              }
            `}
            aria-label="Open full menu"
          >
            {sidebarOpen && (
              <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2px] rounded-b-full bg-gradient-to-r from-[#a78bfa] to-[#818cf8]" />
            )}
            <span className="w-[18px] h-[18px] flex items-center justify-center">
              {sidebarOpen ? (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="4" x2="20" y1="5"  y2="5"  />
                  <line x1="4" x2="16" y1="11" y2="11" />
                  <line x1="4" x2="20" y1="17" y2="17" />
                </svg>
              )}
            </span>
            <span className="text-[9.5px] font-semibold tracking-tight">
              {sidebarOpen ? 'Close' : 'More'}
            </span>
          </button>
        </nav>
      </div>
    </div>
  );
}